import { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardCheck, CheckCircle2 } from 'lucide-react';
import { Card, HStack } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Checkbox } from '@components/base/inputs';
import { Alert, Spinner } from '@components/base/feedback';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
} from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { taskService } from '../api/taskService';
import type { VeosMissingPiece } from '../api/taskService';
import { WorkflowStepTracker } from './WorkflowStepTracker';
import type { WorkflowStepExecutionSummary } from './WorkflowStepTracker';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MissingPiecesCardProps {
  idSin: string | number;
  /**
   * Per-tenant column layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the column config is returned unchanged (byte-identical
   * render). Rule authors target the **stable column keys** below
   * (`recu` / `libelle` / `dateReception`) to rename / hide / reorder /
   * tooltip a column without code. Hiding `recu` makes the list display-only
   * (an explicit, allowed admin choice) — the card still owns the tick state
   * and submit, so the workflow path is never affected.
   */
  _layoutOverrides?: LayoutOverrides;
}

// ---------------------------------------------------------------------------
// Date formatting (unchanged from the pre-migration card)
// ---------------------------------------------------------------------------

const DATE_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatDate(epochMs: number): string {
  return DATE_FORMATTER.format(new Date(epochMs));
}

// ---------------------------------------------------------------------------
// Per-row render context
//
// Custom cell renderers are module-singletons (registered once below), but the
// `recu` checkbox must stay controlled by the card's live `checked` state and
// the `dateReception` cell must reflect a just-ticked row's client-side date.
// Both pieces of per-instance, per-render state are carried on each row object
// under `__ctx`, which the renderers read from `row` at render time. The rows
// are rebuilt every render (see `rows` useMemo) so the closures stay fresh —
// no module-global mutable state, multi-instance safe.
// ---------------------------------------------------------------------------

interface PieceRowContext {
  /** Whether this pending row is locally ticked. */
  ticked: boolean;
  /** Toggle this pending row's tick (client-side only — no API call). */
  onToggle: () => void;
}

interface PieceRow extends VeosMissingPiece {
  __ctx: PieceRowContext;
}

function getRowContext(row: Record<string, unknown>): PieceRowContext | undefined {
  const ctx = (row as Partial<PieceRow>).__ctx;
  return ctx ?? undefined;
}

// ---------------------------------------------------------------------------
// Custom cell renderers
//
// Registered once at module load (mirroring CostSynthesisCard / TaskListCard).
// They read the row's `__ctx` for live tick state + toggle callback and branch
// on `RECU` exactly like the original `PieceRow` did:
//   • received (RECU === 1) → read-only green CheckCircle2 + formatted date
//   • pending  (RECU === 0) → controlled Checkbox (recu) / blank-or-today date
// ---------------------------------------------------------------------------

const RECU_CELL_RENDERER = 'claimflow-missingPieceCheckbox';
const DATE_CELL_RENDERER = 'claimflow-missingPieceDate';

dataListWorkflowRegistry.registerCellRenderer(RECU_CELL_RENDERER, (_value, row) => {
  const piece = row as unknown as VeosMissingPiece;
  if (piece.RECU === 1) {
    return <Icon icon={CheckCircle2} size="sm" color="success" />;
  }
  const ctx = getRowContext(row);
  return (
    <Checkbox
      checked={ctx?.ticked ?? false}
      onChange={() => ctx?.onToggle()}
      aria-label={piece.LIB_PIE}
    />
  );
});

dataListWorkflowRegistry.registerCellRenderer(DATE_CELL_RENDERER, (_value, row) => {
  const piece = row as unknown as VeosMissingPiece;
  const isReceived = piece.RECU === 1;
  const ctx = getRowContext(row);
  const dateLabel = isReceived && piece.DATR_PMD
    ? formatDate(piece.DATR_PMD)
    : ctx?.ticked
      ? formatDate(Date.now())
      : null;

  if (!dateLabel) return null;
  return (
    <Text size="sm" color="muted">
      {dateLabel}
    </Text>
  );
});

// ---------------------------------------------------------------------------
// Config factory
//
// Declarative DataListConfig with the three stable column keys. French labels
// live only in `column.label` (display-only / overridable). `libelle` maps to
// the `LIB_PIE` API field; `recu` / `dateReception` use the custom renderers
// above (they read the whole row, so no fieldMapping entry is needed). The
// list preserves API order (sorting off), shows no toolbar/pager, and renders
// the single short list in one page (parity with the original VStack).
// ---------------------------------------------------------------------------

function createMissingPiecesConfig(rows: PieceRow[]): DataListConfig {
  return {
    id: 'claimflow.missingPieces',

    dataSource: {
      data: rows as unknown as Record<string, unknown>[],
      fieldMapping: { libelle: 'LIB_PIE' },
    },

    columns: {
      recu: {
        label: 'Reçue',
        order: 1,
        align: 'left',
        type: 'custom',
        typeOptions: { renderer: RECU_CELL_RENDERER },
        sortable: false,
      },
      libelle: {
        label: 'Pièce',
        order: 2,
        align: 'left',
        type: 'text',
        sortable: false,
      },
      dateReception: {
        label: 'Date de réception',
        order: 3,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: DATE_CELL_RENDERER },
        sortable: false,
      },
    },

    display: {
      size: 'compact',
      hoverable: false,
    },

    sorting: {
      enabled: false,
    },

    toolbar: {
      visible: false,
    },

    // Single short, fixed list — no pagination footer.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MissingPiecesCard({
  idSin,
  _layoutOverrides,
}: Readonly<MissingPiecesCardProps>) {
  const [data, setData] = useState<VeosMissingPiece[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [failedSteps, setFailedSteps] = useState<WorkflowStepExecutionSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pieces = await taskService.fetchMissingPieces(Number(idSin));
        if (!cancelled) {
          setData(pieces);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erreur de chargement.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idSin]);

  const refetch = useCallback(async () => {
    try {
      const pieces = await taskService.fetchMissingPieces(Number(idSin));
      setData(pieces);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement.');
    }
  }, [idSin]);

  const receivedCount = data?.filter((p) => p.RECU === 1).length ?? 0;
  const tickedCount = Object.values(checked).filter(Boolean).length;
  const totalCount = data?.length ?? 0;

  const handleSubmit = useCallback(async () => {
    const idPmds = Object.entries(checked)
      .filter(([, v]) => v)
      .map(([id]) => Number(id));
    if (idPmds.length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setFailedSteps([]);
    try {
      await taskService.receiveMissingPieces(Number(idSin), idPmds);
      await refetch();
      setChecked({});
      setSubmitted(true);
      // Re-fold on success (the card may have been expanded by the user); the
      // `key` swap below remounts the Card so it re-reads `defaultExpanded`.
      setExpanded(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Échec de l'enregistrement.");
      setFailedSteps(extractStepExecutions(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [checked, idSin, refetch]);

  // Build the per-render rows (data + live tick context) and the merged config.
  // Rows are rebuilt whenever the data or tick state changes so the renderers'
  // closures stay fresh; `applyLayoutOverrides` returns the config unchanged at
  // baseline (no rules) so the render is byte-identical.
  const rows = useMemo<PieceRow[]>(() => {
    return (data ?? []).map((piece) => ({
      ...piece,
      __ctx: {
        ticked: !!checked[piece.ID_PMD],
        onToggle: () => {
          setChecked((prev) => ({ ...prev, [piece.ID_PMD]: !prev[piece.ID_PMD] }));
        },
      },
    }));
  }, [data, checked]);

  const baseConfig = useMemo(() => createMissingPiecesConfig(rows), [rows]);

  usePublishLayoutConfig(
    'claimflow.missingPiecesCard',
    buildPublishedConfigFromDataList(baseConfig, 'Pièces MQ reçues'),
  );

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const list = useDataList(config);

  // Loading state — collapsed card with header spinner
  if (isLoading) {
    return (
      <Card id={actionAnchorId('claimflow.missingPiecesCard')}>
        <Card.Header collapsible defaultExpanded={false} action={<Spinner size="sm" />}>
          <Icon icon={ClipboardCheck} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Pièces MQ reçues', _layoutOverrides)}
          </Text>
        </Card.Header>
      </Card>
    );
  }

  // Error state — load failed
  if (error) {
    return (
      <Card id={actionAnchorId('claimflow.missingPiecesCard')}>
        <Card.Header collapsible defaultExpanded={true}>
          <Icon icon={ClipboardCheck} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Pièces MQ reçues', _layoutOverrides)}
          </Text>
        </Card.Header>
        <Card.Content>
          <Alert variant="error">{error}</Alert>
        </Card.Content>
      </Card>
    );
  }

  // Empty list — render nothing
  if (!data || data.length === 0) {
    return null;
  }

  const showSuccess = submitted && tickedCount === 0;

  return (
    <Card key={showSuccess ? 'submitted' : 'form'} id={actionAnchorId('claimflow.missingPiecesCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        {showSuccess ? (
          <Icon icon={CheckCircle2} size="md" color="success" />
        ) : (
          <Icon icon={ClipboardCheck} size="md" color="primary" />
        )}
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Pièces MQ reçues', _layoutOverrides)}
        </Text>
        {showSuccess && (
          <Text as="span" size="sm" color="muted" className="ml-2">
            — {receivedCount}/{totalCount} reçues
          </Text>
        )}
      </Card.Header>
      <Card.Content>
        {submitError && (
          <div className="mb-4">
            <Alert variant="error">{submitError}</Alert>
          </div>
        )}
        {failedSteps.length > 0 && (
          <div className="mb-4">
            <Text size="sm" weight="semibold" className="mb-2">
              Détail de l&apos;exécution :
            </Text>
            <WorkflowStepTracker steps={failedSteps} />
          </div>
        )}
        <DataList config={config} list={list}>
          <DataList.Table />
        </DataList>
        <HStack justify="between" align="center" className="pt-4">
          <Text size="sm" color="muted">
            {receivedCount + tickedCount}/{totalCount} pièces reçues
          </Text>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={tickedCount === 0 || isSubmitting}
          >
            Enregistrer
          </Button>
        </HStack>
      </Card.Content>
    </Card>
  );
}

function extractStepExecutions(error: unknown): WorkflowStepExecutionSummary[] {
  const data = getErrorData(error);
  if (!data) return [];

  const executions = (data as Record<string, unknown>).step_executions;
  if (!Array.isArray(executions)) return [];

  return executions.map((exec: Record<string, unknown>) => ({
    name:
      ((exec.step as Record<string, unknown>)?.name as string) ??
      `Étape ${String(exec.workflow_step_id).slice(0, 8)}`,
    status: exec.status as WorkflowStepExecutionSummary['status'],
    errorMessage: (exec.error_message as string | null) ?? null,
  }));
}

function getErrorData(error: unknown): unknown {
  if (error === null || typeof error !== 'object') return null;
  const err = error as Record<string, unknown>;

  const response = err.response;
  if (response !== null && typeof response === 'object') {
    const respObj = response as Record<string, unknown>;
    const respData = respObj.data;
    if (respData !== null && typeof respData === 'object') {
      const nested = (respData as Record<string, unknown>).data;
      if (nested !== null && typeof nested === 'object') return nested;
      return respData;
    }
  }

  const data = err.data;
  if (data !== null && typeof data === 'object') return data;

  return null;
}
